import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { postLead } from "@/lib/api";
import { getVisitSessionId } from "@/attribution";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAGE_BOOK_FLAG = "bt_cage_from_book";
const CAGE_THIRTY_MIN = 30 * 60 * 1000;
const CAGE_GOOGLE_RE = /^google\.(com|[a-z]{2}|co\.[a-z]{2}|com\.[a-z]{2})$/;
const cageDomainMatch = (h, d) => h === d || h.endsWith("." + d);

// Debounce guard: prevents duplicate pings from a single page load (React re-renders /
// StrictMode double-invoke in dev). Real re-navigations happen seconds apart and still count.
let lastCagePingAt = 0;

// Classify how this session arrived at /cage-system, from the live signals (not stored attribution).
function classifyCageArrival() {
  try {
    const raw = sessionStorage.getItem(CAGE_BOOK_FLAG);
    if (raw) {
      const rec = JSON.parse(raw);
      if (rec && rec.t && Date.now() - rec.t < CAGE_THIRTY_MIN) return { bucket: "BOOK", host: "" };
    }
  } catch (e) { /* ignore */ }
  try {
    const utm = (new URLSearchParams(window.location.search).get("utm_source") || "").trim().toLowerCase();
    if (utm === "email") return { bucket: "EMAIL", host: "" };
  } catch (e) { /* ignore */ }
  let host = "";
  try {
    const u = new URL(document.referrer || "");
    if (u.protocol === "http:" || u.protocol === "https:") host = (u.hostname || "").toLowerCase().replace(/^www\./, "");
  } catch (e) { /* ignore */ }
  if (!host) return { bucket: "DIRECT", host: "" };
  if (cageDomainMatch(host, "thebiologytrader.com")) return { bucket: "WEBSITE", host: "" };
  if (CAGE_GOOGLE_RE.test(host)) return { bucket: "GOOGLE", host: "" };
  return { bucket: "OTHER", host: host.replace(/[^a-z0-9._-]/g, "").slice(0, 100) };
}

const AUTHOR_NOTE_URL =
  "/author-note.png?v=3";

const TOOLS = [
  { name: "Live Spiral Protocol", desc: "what you do the moment a spiral starts, decided before it starts.", icon: "/icons/tool-spiral.png" },
  { name: "State Filter", desc: "the pre-session go/no-go decision, made before the platform opens.", icon: "/icons/tool-filter.png" },
  { name: "Size Lock", desc: "what size you're permitted today, and what removes that permission.", icon: "/icons/tool-lock.png" },
  { name: "Consistency Ledger", desc: "the running record of rule compliance, kept whether it flatters you or not.", icon: "/icons/tool-ledger.png" },
];

export default function CageSystem() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [error, setError] = useState("");
  const [earlyAccessEnabled, setEarlyAccessEnabled] = useState(false);
  const arrivalRef = useRef(null);
  if (arrivalRef.current === null) arrivalRef.current = classifyCageArrival();

  useEffect(() => {
    const now = Date.now();
    if (now - lastCagePingAt < 2000) return;
    lastCagePingAt = now;
    const a = arrivalRef.current;
    const sid = getVisitSessionId();
    if (!sid) return;
    fetch(`${API}/track/cage-visit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sid, bucket: a.bucket, host: a.host }),
      keepalive: true,
    }).catch(() => {});
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/public-settings`)
      .then((r) => setEarlyAccessEnabled(Boolean(r.data?.cage_early_access_enabled)))
      .catch(() => setEarlyAccessEnabled(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    setError("");
    try {
      await postLead(email.trim(), "cage_system");
      setStatus("done");
      setEmail("");
    } catch {
      setError("Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  return (
    <div data-testid="page-cage-system">
      <section>
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 pt-20 md:pt-32 pb-20 md:pb-32">
          <div className="label-mono text-muted" data-testid="cage-system-kicker">The Complete Implementation</div>
          <h1
            className="font-display mt-8 text-[2.5rem] sm:text-5xl md:text-7xl lg:text-[6rem] leading-[1.02] tracking-tight max-w-6xl"
            data-testid="cage-system-h1"
          >
            The Cage System
          </h1>
          <p className="mt-6 text-ink text-lg md:text-xl" data-testid="cage-system-ship-date">
            Launches 24 October 2026.
            <br />
            <span className="text-sm md:text-base text-muted">In production since 28 June 2026.</span>
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-12 md:gap-16">
            <p
              className="md:col-span-7 text-muted text-lg md:text-xl leading-[1.5] md:leading-[1.85]"
              data-testid="cage-system-intro"
            >
              <Link to="/ebook" className="underline">The book</Link> gives you the complete argument and the architecture. <span className="whitespace-nowrap">The Cage System</span> is the calibrated implementation of it: decision rights, permissions and consequences as a working workflow, for traders who want the system without having to remember&nbsp;it.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-12 md:gap-16">
            <ul className="md:col-span-7 border-t border-hairline divide-y divide-hairline" data-testid="cage-system-tools">
            {TOOLS.map((t) => (
              <li key={t.name} className="py-8 flex items-center justify-between gap-6" data-testid={`cage-system-tool-${t.name.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-ink font-semibold text-xl">{t.name}</p>
                  <p className="mt-2 text-muted">{t.desc}</p>
                </div>
                <img
                  src={t.icon}
                  alt=""
                  aria-hidden="true"
                  className="w-9 h-9 md:w-10 md:h-10 shrink-0 object-contain"
                  data-testid={`cage-system-tool-icon-${t.name.toLowerCase().replace(/\s/g, "-")}`}
                />
              </li>
            ))}
            </ul>
          </div>

          {earlyAccessEnabled && (
          <div className="mt-20 max-w-lg" data-testid="cage-system-signup">
            {status === "done" ? (
              <p className="text-ink text-lg" data-testid="cage-system-signup-success">
                You're on the list. Watch your inbox.
              </p>
            ) : (
              <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 border border-hairline bg-white px-4 py-3 text-ink focus:outline-none focus:border-ink"
                  data-testid="cage-system-email-input"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="label-mono bg-ink text-white px-6 py-3 hover:bg-black transition-colors disabled:opacity-60"
                  data-testid="cage-system-submit"
                >
                  {status === "loading" ? "Joining…" : "Join the early list"}
                </button>
              </form>
            )}
            {error && (
              <p className="mt-3 text-sm text-red-600" data-testid="cage-system-signup-error">{error}</p>
            )}
            <p className="mt-4 label-mono text-muted" data-testid="cage-system-signup-note">
              This list gets first access on launch day, before it goes public.
            </p>
          </div>
          )}

          <div className="mt-12 grid grid-cols-1 md:grid-cols-12 md:gap-16" data-testid="cage-system-why-four">
            <div className="md:col-span-7">
              <div className="label-mono text-muted">Why Four</div>
              <div className="mt-6 text-muted text-lg md:text-xl leading-[1.5] md:leading-[1.85] space-y-6">
                <p>The tools were going to come separately. I stopped when I couldn't describe one of them without using the other three. Every one is a permission that another tool grants or removes.</p>
                <p>All four, because the version of you who needs stopping doesn't get to choose which one to skip. You need a system that works completely.</p>
              </div>
            </div>
          </div>


          <div className="mt-20 grid grid-cols-1 md:grid-cols-12 md:gap-16" data-testid="cage-system-why-october">
            <div className="md:col-span-7">
              <div className="label-mono text-muted">Why October</div>
              <h2 className="font-display mt-6 text-3xl md:text-4xl lg:text-[2.75rem] tracking-tight" style={{ lineHeight: 1.12 }}>
                The system already runs. Making it transferable takes time.
              </h2>
              <div className="mt-10 text-muted text-lg md:text-xl leading-[1.5] md:leading-[1.85] space-y-6">
                <p>These four tools are not a concept. In their original spreadsheet form, they have governed my own trading for the past two years. They work because I built them around my rules, thresholds, shorthand and decisions I no longer notice I am making.</p>
                <p>The work between now and 24 October is turning that private operating system into something another trader can understand, calibrate and use without me beside them: making the implicit explicit, documenting the thresholds, testing the sequence and deciding what must be visible when a rule binds.</p>
              </div>
            </div>
          </div>
          <div className="mt-20" data-testid="cage-system-the-model">
            <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-16">
              <div className="md:col-span-7">
                <div className="label-mono text-muted">The Model</div>
                <h2 className="font-display mt-6 text-3xl md:text-4xl lg:text-[2.75rem] tracking-tight max-w-3xl" style={{ lineHeight: 1.12 }}>
                  Start from mine.
                </h2>
                <div className="mt-10 text-muted text-lg md:text-xl leading-[1.5] md:leading-[1.85] space-y-6 max-w-3xl">
                  <p>The goal is to deliver the strongest version of the system I can: one that helps you improve execution, reduce destructive errors and move toward consistent profitability.</p>
                  <p>You can build your own Cage from the book. The complete architecture is there to be adapted to your own trading. The version I use came from six years of knowing how to trade but still failing to keep the money, followed by nine months of building the core system from 168 journal entries and two years of running it under real pressure.</p>
                  <p>From 24 October 2026, you can begin with the finished Cage System, tested and refined through real trading. The paid version brings the permissions, limits, decision pages and session process together in one connected operating structure, ready to use.</p>
                </div>
              </div>
              <div className="md:col-span-5" data-testid="cage-system-author-note">
                <div className="hidden md:block invisible" aria-hidden="true">
                  <div className="label-mono">The Model</div>
                  <h2 className="font-display mt-6 text-3xl md:text-4xl lg:text-[2.75rem] tracking-tight" style={{ lineHeight: 1.12 }}>
                    Start from mine.
                  </h2>
                </div>
                <img
                  src={AUTHOR_NOTE_URL}
                  alt="A note from the author: For six years I had a version of this conversation in my head and never once out loud. Everyone around me knew I traded. Nobody knew what it was costing me to keep doing it."
                  className="block w-full max-w-sm h-auto md:mt-10"
                  data-testid="cage-system-author-note-img"
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 md:gap-16">
            <div className="md:col-span-7">
              <h2 className="font-display text-ink mt-8 mb-6 text-3xl md:text-4xl lg:text-[2.75rem] tracking-tight" style={{ lineHeight: 1.12 }} data-testid="cage-system-closing-statement">
                The edge earns it, the spiral takes&nbsp;it&nbsp;back.
              </h2>
              <div className="text-muted text-lg md:text-xl leading-[1.5] md:leading-[1.85] space-y-6" data-testid="cage-system-closing-body">
                <p>Stop the losing cycle before it damages your equity curve again. After all these years, it is time to see how your edge performs when the spiral is no longer allowed to undo it. Knowledge alone does not produce execution.<br /><strong className="font-semibold">A structure built to hold under pressure does.</strong></p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
